import cx from 'classnames';
import { useEffect, useRef, useState } from 'react';

import Button from '../button/button.component';
import '../../styles/form-fields.styles.scss';
import './form-delivery.styles.scss';

// `optional` ne concerne que le complément d'adresse : c'est le seul champ que
// `isCompleteForm` laisse vide sans bloquer la commande.
const FIELDS = [
  {
    name: 'email',
    label: 'Email',
    type: 'email',
    error: 'Entrez une adresse e-mail valide',
    wide: true
  },
  { name: 'fullName', label: 'Nom', error: 'Saisir votre nom et prénom' },
  {
    name: 'address',
    label: 'Adresse',
    error: 'Saisir une adresse',
    wide: true
  },
  {
    name: 'additionalAddress',
    label: "Complément d'adresse",
    optional: true,
    wide: true
  },
  {
    name: 'postalCode',
    label: 'Code postal',
    type: 'number',
    error: 'Saisir un code postal'
  },
  { name: 'city', label: 'Ville', error: 'Saisir une ville' },
  {
    name: 'phone',
    label: 'Téléphone',
    type: 'number',
    error: 'Saisissez un numéro de téléphone pour ce mode de livraison'
  },
  { name: 'country', label: 'Pays', error: 'Saisir un pays' }
];

// Prédicat pur, hors composant : il ne dépend que de son argument, donc l'effet
// qui l'appelle n'a pas à le lister en dépendance.
const isCompleteForm = (customerInfos) => {
  for (const field in customerInfos) {
    if (field !== 'additionalAddress' && customerInfos[field] === '') {
      return false;
    }
  }
  return true;
};

const FormContact = ({ cartItems, promoCode }) => {
  const [isTouchedFields, setIsTouchedFields] = useState({});
  const [isDisabled, setIsDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const submittingRef = useRef(false);

  const [customerInfos, setCustomerInfos] = useState({
    email: '',
    fullName: '',
    address: '',
    additionalAddress: '',
    postalCode: '',
    city: '',
    phone: '',
    country: ''
  });

  const {
    email,
    fullName,
    address,
    additionalAddress,
    postalCode,
    city,
    phone,
    country
  } = customerInfos;

  useEffect(() => {
    setIsDisabled(!isCompleteForm(customerInfos));
  }, [customerInfos]);

  // On n'envoie que des références : le serveur recalcule le prix depuis son catalogue.
  // Pour une fleur/hasch, on transmet le palier choisi (priceValue), que le serveur valide.
  const buildItems = () =>
    cartItems.map((item) =>
      item.isFlorOrHasch
        ? {
            // L'id d'une fleur au panier encode le palier en décimale (ex: 417.2).
            // On renvoie l'id ENTIER réel ; le serveur valide le palier via priceValue.
            id: Math.trunc(item.id),
            quantity: item.quantity,
            priceValue: item.price
          }
        : { id: item.id, quantity: item.quantity }
    );

  const handleChange = (e) => {
    const name = e.target.name;
    const value = e.target.value;
    setCustomerInfos((prev) => ({ ...prev, [name]: value }));
  };

  const handleBlur = (e) => {
    const name = e.target.name;
    setIsTouchedFields((prev) => ({ ...prev, [name]: true }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Anti double-soumission : le ref bloque un 2e clic avant même le re-render.
    if (submittingRef.current) {
      return;
    }

    const items = buildItems();
    if (items.length === 0) {
      return;
    }

    submittingRef.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // URL relative → toujours same-origin (marche sur www ET sans-www, évite le blocage CORS).
      // Extension .php explicite : sans elle, le SPA fallback du .htaccess renverrait index.html.
      const response = await fetch('/getOrderId.php', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items,
          promoCode,
          customer: {
            email,
            fullName,
            phone,
            address,
            additionalAddress,
            postalCode,
            city,
            country
          }
        })
      });

      if (!response.ok) {
        const errorBody = await response.json().catch(() => null);
        console.error(
          'Erreur création de commande',
          response.status,
          errorBody
        );
        setSubmitError(
          'Une erreur est survenue lors de la validation de votre commande. Merci de réessayer.'
        );
        submittingRef.current = false;
        setIsSubmitting(false);
        return;
      }

      // L'URL de paiement (demo ou production) est décidée par le serveur (config.php).
      const { checkoutUrl } = await response.json();

      if (checkoutUrl) {
        // On quitte la page vers Viva → on laisse le bouton désactivé.
        window.location.href = checkoutUrl;
      } else {
        setSubmitError('Réponse inattendue du serveur. Merci de réessayer.');
        submittingRef.current = false;
        setIsSubmitting(false);
      }
    } catch (error) {
      console.log('ERROR', error);
      setSubmitError(
        'Connexion au paiement impossible. Vérifiez votre connexion internet et réessayez.'
      );
      submittingRef.current = false;
      setIsSubmitting(false);
    }
  };

  return (
    <div>
      <h2 className="delivery-block-title">Mode de livraison</h2>

      <label className="delivery-shipping-option">
        <input type="radio" name="shipping" defaultChecked readOnly />
        <span>Standard — Colissimo</span>
      </label>

      <h2 className="delivery-block-title">Coordonnées et adresse</h2>

      <form className="brand-form">
        <div className="delivery-fields">
          {FIELDS.map(({ name, label, type, error, optional, wide }) => (
            <label className={cx('form-field', { 'is-wide': wide })} key={name}>
              <span className="form-field-label">
                {optional ? label : `${label} *`}
              </span>
              <input
                name={name}
                type={type}
                value={customerInfos[name]}
                onChange={handleChange}
                onBlur={optional ? undefined : handleBlur}
              />
              <span className="form-field-error">
                {!customerInfos[name] && isTouchedFields[name] && error}
              </span>
            </label>
          ))}
        </div>

        {submitError && <p className="form-field-error">{submitError}</p>}

        <Button
          className="delivery-submit"
          onClick={handleSubmit}
          disabled={isDisabled || isSubmitting}
          type="submit"
        >
          {isSubmitting ? 'Redirection…' : 'Continuer vers le paiement'}
        </Button>
      </form>
    </div>
  );
};
export default FormContact;
